import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import {
  PayoutData,
  Service,
  services,
  useSubmit,
} from '@kanda-libs/ks-frontend-services';

import type { FormattedTableJob } from 'components/job/JobTable/types';
import companySelectors from 'store/selectors/company';

import { encodeData, formatPayoutData, getFileName } from './helpers';

export interface DownloadCsvHook {
  onClick: () => void;
  isSubmitting: boolean;
}

export default function useDownloadCsv(
  data: FormattedTableJob[],
): DownloadCsvHook {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { showError } = useToast();
  const unformattedCompanies = useSelector(companySelectors.getEntitiesAsArray);

  const { submit: getPayouts } = useSubmit(
    services.payouts.payouts as unknown as Service<
      StringIndexedObject,
      StringIndexedObject,
      StringIndexedObject
    >,
  );

  const successCallback = useCallback(
    (payoutData: PayoutData[]) => {
      const toExport = formatPayoutData(payoutData);
      const content = encodeData(toExport);
      const element = document.createElement('a');
      element.setAttribute(
        'href',
        URL.createObjectURL(
          new Blob([content], {
            type: 'text/csv;encoding:utf-8',
          }),
        ),
      );
      element.setAttribute('download', getFileName());
      element.click();
      element.remove();
      setIsSubmitting(false);
    },
    [data, unformattedCompanies, showError],
  );

  const onClick = useCallback(() => {
    setIsSubmitting(true);
    getPayouts({}).then(({ data, error }) => {
      if (error) {
        showError('Error getting payouts from cloud function');
        setIsSubmitting(false);
        return;
      }
      const payoutData: PayoutData[] = data?.data;
      if (!payoutData) {
        showError('Error getting payouts from cloud function');
        setIsSubmitting(false);
        return;
      }
      successCallback(payoutData);
    });
  }, [successCallback]);

  return {
    onClick,
    isSubmitting,
  };
}
